/**
 * 钱包签名 Hook
 * 提供获取挑战和签名的通用功能
 */

import { useCallback } from 'react';
import {
  getAppChallenge,
  getAppRpcChallenge,
  getWalletChallenge,
  type ApiResult,
  type RpcChallengeAction,
} from '@/api';
import { useAuthStore } from '@/stores';
import {
  WalletError,
  classifyWalletError,
  isUserRejected,
} from '@/utils/walletError';
import { resolveLiveEip1193Provider } from '@/utils/walletProvider';

// Re-export so existing call sites (`Keys` / `Dashboard`) keep importing
// `isUserRejected` from this hook while the implementation lives in the shared
// wallet error toolkit.
export { isUserRejected };

/**
 * 签名结果
 */
export interface SignatureResult {
  /** 签名数据 */
  signature: string;
  /** 挑战消息 */
  message: string;
}

type ChallengeFn = () => Promise<ApiResult<{ message: string }>>;

/** Prefer the authenticated wallet when the extension returns multiple accounts. */
function pickSigningAddress(accounts: string[]): string {
  const authAddress = useAuthStore.getState().userInfo?.walletAddress;
  if (authAddress) {
    const match = accounts.find(
      (a) => a.toLowerCase() === authAddress.toLowerCase()
    );
    if (match) return match;
  }
  return accounts[0];
}

/**
 * 钱包签名 Hook
 *
 * 提供挑战来源：
 * - `getSignature()`：使用 `/apps/challenge`（应用注册场景，原有兼容）
 * - `getAppRpcSignature(appId, action)`：使用 `/apps/{appId}/rpc/challenge`
 * - `getWalletSignature()`：使用 `/wallet/challenge`（订阅变更 / 提现场景）
 *
 * Provider resolution uses {@link resolveLiveEip1193Provider} (kit provider or
 * injected `window.ethereum`) — never opens WalletKit Connect Wallet UI.
 *
 * @example
 * ```ts
 * const { getSignature, getAppRpcSignature, getWalletSignature } = useWalletSignature();
 *
 * // 应用注册
 * const sig1 = await getSignature();
 *
 * // 启用 RPC
 * const sigRpc = await getAppRpcSignature(appId, 'enable');
 *
 * // 订阅取消 / 提现
 * const sig2 = await getWalletSignature();
 * if (sig2) {
 *   await cancelSubscription({ signature: sig2.signature });
 * }
 * ```
 */
export const useWalletSignature = () => {
  const sign = useCallback(
    async (challengeFn: ChallengeFn): Promise<SignatureResult | null> => {
      // Kit provider or window.ethereum — no WalletKit connect() modal.
      const activeProvider = await resolveLiveEip1193Provider();

      const challengeResult = await challengeFn();
      // Backend challenge failure is not a wallet error: keep the legacy
      // null return so callers fall back to their generic "operation failed".
      if (!challengeResult.ok) {
        console.error(
          '[WalletSignature] Failed to get challenge:',
          challengeResult.error
        );
        return null;
      }

      const { message } = challengeResult.data;

      try {
        const accounts = (await activeProvider.request({
          method: 'eth_requestAccounts',
        })) as string[];
        if (!accounts || accounts.length === 0) {
          throw new WalletError('noProvider');
        }

        const address = pickSigningAddress(accounts);

        const signature = await activeProvider.request({
          method: 'personal_sign',
          params: [message, address],
        });

        return {
          signature: signature as string,
          message,
        };
      } catch (error) {
        // Past the challenge any failure is wallet-layer (user rejection, dead
        // extension, hung request, ...). Rethrow as a classified WalletError so
        // callers can show a precise i18n message and abort cleanly.
        if (error instanceof WalletError) throw error;
        console.error('[WalletSignature] Error getting signature:', error);
        throw new WalletError(classifyWalletError(error));
      }
    },
    []
  );

  /** 通过 /apps/challenge 获取签名（应用注册场景） */
  const getSignature = useCallback(
    () => sign(getAppChallenge),
    [sign]
  );

  /**
   * 通过 /apps/{appId}/rpc/challenge 获取签名
   *
   * @param appId - 应用 ID
   * @param action - `enable`（默认）或 `rotate`
   */
  const getAppRpcSignature = useCallback(
    (appId: string, action: RpcChallengeAction = 'enable') =>
      sign(() => getAppRpcChallenge(appId, { action })),
    [sign]
  );

  /** 通过 /wallet/challenge 获取签名（订阅 / 提现场景） */
  const getWalletSignature = useCallback(
    () => sign(getWalletChallenge),
    [sign]
  );

  /** 对调用方已获取的挑战消息签名。 */
  const signMessage = useCallback(
    (message: string) =>
      sign(async () => ({ ok: true, data: { message } })),
    [sign]
  );

  return {
    getSignature,
    getAppRpcSignature,
    getWalletSignature,
    signMessage,
  };
};
